import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AsyncUserFinderFetcher } from '@app/modules/user/fetchers/async-user-finder-fetcher';
import { UserResponseDTO } from '@app/modules/user/interfaces/user-response-dto.interface';
import { Token } from '@interfaces/core/token.interface';
import { LocalStorageHandler } from '@services/local-storage-handler.service';

import { authConfig } from '../config';
import { AuthTokenSignalHandler } from '../signal-handlers/auth-token-signal-handler';
import { AuthUserAuthenticatedSignalHandler } from '../signal-handlers/auth-user-authenticated-signal-handler';
import { AuthSessionDeleter } from './auth-user-session-deleter.service';
import { TokenChecker } from './token-checker.service';
import { TokenExpiryChecker } from './token-expiry-checker.service';
import { TokenRefresher } from './token-refresher.service';

const sections = authConfig.actions.userAuthentication.sections;

@Injectable()
export class TokenLoader {
  constructor(
    private readonly userAuthenticationSignalHandler: AuthUserAuthenticatedSignalHandler,
    private readonly tokenSignalHandler: AuthTokenSignalHandler,
    private readonly localStorageUserHandler: LocalStorageHandler<UserResponseDTO>,
    private readonly localStorageTokenHandler: LocalStorageHandler<Token>,
    private readonly tokenExpiryChecker: TokenExpiryChecker,
    private readonly authSessionDeleter: AuthSessionDeleter,
    private readonly tokenRefresher: TokenRefresher,
    private readonly tokenChecker: TokenChecker,
    private readonly router: Router,
    private readonly asyncUserFinderFetcher: AsyncUserFinderFetcher,
  ) {}

  public async run(): Promise<void> {
    const tokens = this.getTokensFromStorage();
    const user = this.getUserFromStorage();
    if (!tokens || !user) {
      return;
    }
    this.userAuthenticationSignalHandler.setUserAuthenticated(user);
    if (!this.tokenChecker.isTokenActive()) {
      this.authSessionDeleter.run();
    }

    // this.localStorageTokenHandler.insert(sections.authToken, tokens);

    this.tokenSignalHandler.setToken(tokens);
    await this.getUserData(tokens.accessToken);
    this.localStorageUserHandler.insert(sections.authUser, user);
    this.tokenRefresher.listenTokenRefresh(tokens);
  }

  private async getUserData(accessToken: string) {
    const userResponse = await this.asyncUserFinderFetcher.run(accessToken);
    if (!userResponse.body) {
      return;
    }

    this.storeUser(userResponse.body);
  }

  private storeUser(user: UserResponseDTO) {
    this.localStorageUserHandler.insert(sections.authUser, user);
    this.userAuthenticationSignalHandler.setUserAuthenticated(user);
  }

  private getTokensFromStorage(): Token {
    return this.localStorageTokenHandler.read(sections.authToken);
  }

  private getUserFromStorage(): UserResponseDTO {
    return this.localStorageUserHandler.read(sections.authUser);
  }
}
